import React from 'react'
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Grid } from '@mui/material'
import { useForm } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { usePostSalesCommission } from '../../api/sales-commission'
import FileField from '../../component/shared/component/FileFieldV2'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'

const SUPPORTED_FORMATS = { 'application/pdf': ['.pdf'] }

const FILE_SIZE = 3 * 1000 * 1000 //3MB

const validationSchema = yup.object().shape({
  commission: yup
    .array()
    .min(1, 'At least one supporting document is required')
    .test('fileSize', 'Each file must be smaller than 3 MB', (files) => {
      if (!files) return false
      return files.every((file) => file.size <= FILE_SIZE)
    }),
})

const Import = ({ setOpen, open, refetch }) => {
  const {
    handleSubmit,
    reset,
    formState: { errors },
    register,
    setValue,
    trigger,
  } = useForm({
    resolver: yupResolver(validationSchema),
  })

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const { mutateAsync, isLoading } = usePostSalesCommission()

  const onClose = () => {
    reset()
    setOpen(false)
  }

  const onSubmit = async (values) => {
    const formData = new FormData()
    for (let j = 0; j < values.commission.length; j++)
      formData.append('commission', values.commission[j])

    try {
      const response = await mutateAsync(formData)
      await refetch()

      const { data = {} } = response || {}

      const { insertedCount = 0, skippedCount = 0, duplicateEntries = [] } = data

      const duplicateLines = duplicateEntries.map(
        (d) => `• ${d.salesId} — ${d.htgCompany} (${d.monthYear})`
      )

      const successMessage = [
        `Successfully imported ${insertedCount} record${insertedCount !== 1 ? 's' : ''}.`,
        skippedCount > 0
          ? `Skipped ${skippedCount} duplicate entr${
              skippedCount === 1 ? 'y' : 'ies'
            }:\n${duplicateLines.join('\n')}`
          : null,
      ]
        .filter(Boolean)
        .join('\n')

      enqueueSnackbar(successMessage, {
        variant: 'success',
        persist: true,
        style: { whiteSpace: 'pre-line' },
        action: (key) => (
          <IconButton onClick={() => closeSnackbar(key)} color='inherit' size='small'>
            <CloseIcon fontSize='small' />
          </IconButton>
        ),
      })

      onClose()
    } catch (e) {
      const errorData = e?.response?.data?.errors || []
      const message =
        errorData.length > 0
          ? errorData.map((err) => `• ${err.msg}`).join('\n')
          : 'Something went wrong. Please try again.'

      enqueueSnackbar(message, {
        variant: 'error',
        persist: true,
        style: { whiteSpace: 'pre-line' },
        action: (key) => (
          <IconButton onClick={() => closeSnackbar(key)} color='inherit' size='small'>
            <CloseIcon fontSize='small' />
          </IconButton>
        ),
      })
    }
  }

  return (
    <Dialog open={open} fullScreen>
      <DialogTitle>Import Sales Commission</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FileField
                inputProps={{
                  ...register('commission'),
                  onChange: (e) => {
                    setValue('commission', e?.target?.files)
                    trigger('commission')
                  },
                  type: 'file',
                  accept: SUPPORTED_FORMATS,
                  multiple: true,
                }}
                error={errors?.commission && true}
                helperText={errors?.commission && errors?.commission?.message}
                label={'Sales Commission'}
                required={true}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-start' }}>
          <Button variant='outlined' onClick={onClose} disabled={isLoading}>
            CANCEL
          </Button>
          <Button type='submit' variant='contained' color='primary' disabled={isLoading}>
            SUBMIT
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default Import
